import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Search, ChevronDown, ChevronUp, BookOpen, CreditCard, QrCode, Truck, Users, Settings } from "lucide-react";
import { Input } from "@/components/ui/input";

const faqCategories = [
  {
    icon: BookOpen,
    title: "Primeiros Passos",
    faqs: [
      { q: "Como criar minha conta?", a: "Acesse a página de cadastro, preencha os dados do seu restaurante e confirme o e-mail. Seu cardápio digital estará pronto em minutos." },
      { q: "Preciso instalar algum aplicativo?", a: "Não! O MenuPro funciona 100% pelo navegador. Seus clientes também acessam o cardápio pelo celular sem instalar nada." },
      { q: "Posso testar antes de assinar?", a: "Sim! Oferecemos um plano gratuito para você experimentar as funcionalidades básicas sem compromisso." },
    ],
  },
  {
    icon: QrCode,
    title: "Cardápio & QR Code",
    faqs: [
      { q: "Como gerar QR Codes para as mesas?", a: "No painel, acesse Configurações > QR Codes. Defina a quantidade de mesas e baixe os QR Codes em PDF para impressão." },
      { q: "Posso personalizar o visual do cardápio?", a: "Sim! Em Configurações > Aparência você pode alterar cores, logo, fonte e layout do cardápio digital." },
      { q: "Como adicionar um produto com variações?", a: "Ao cadastrar um produto, clique em 'Adicionais' para criar opções como tamanhos, sabores extras e acompanhamentos." },
    ],
  },
  {
    icon: CreditCard,
    title: "Pagamentos",
    faqs: [
      { q: "Quais formas de pagamento são aceitas?", a: "PIX (QR Code automático), cartão de crédito, cartão de débito, dinheiro e MercadoPago." },
      { q: "Como configurar o MercadoPago?", a: "Em Configurações > Pagamento, insira suas credenciais do MercadoPago (Access Token e Public Key)." },
      { q: "As taxas do MercadoPago ficam por minha conta?", a: "Sim, as taxas de processamento são as padrão do MercadoPago. O MenuPro não cobra taxa adicional sobre transações." },
    ],
  },
  {
    icon: Truck,
    title: "Delivery",
    faqs: [
      { q: "Como configurar o delivery?", a: "Em Configurações > Delivery, defina o raio de entrega, taxa fixa ou por distância, valor mínimo e cadastre seus entregadores." },
      { q: "O cliente consegue rastrear o pedido?", a: "Sim! Após o pedido ser confirmado, o cliente recebe um link de acompanhamento com status em tempo real." },
      { q: "Posso usar delivery sem entregador próprio?", a: "Atualmente o delivery funciona com entregadores cadastrados por você. Integração com apps de entrega está em desenvolvimento." },
    ],
  },
  {
    icon: Users,
    title: "Clientes & Fidelidade",
    faqs: [
      { q: "Como funciona o programa de fidelidade?", a: "Você pode criar programas de pontos, selos (cartão fidelidade digital) ou cashback. Os clientes acumulam automaticamente a cada pedido." },
      { q: "Consigo ver o histórico de pedidos de um cliente?", a: "Sim! Na seção Clientes do painel, você acessa o histórico completo, total gasto e frequência de pedidos de cada cliente." },
      { q: "Como reativar clientes inativos?", a: "Na aba Reativação da página Clientes, o sistema identifica clientes inativos há 30+ dias e gera cupons exclusivos para envio via WhatsApp." },
    ],
  },
  {
    icon: Settings,
    title: "Conta & Planos",
    faqs: [
      { q: "Como alterar meu plano?", a: "No painel, acesse Configurações e selecione o novo plano desejado. A mudança entra em vigor imediatamente." },
      { q: "Posso cancelar a qualquer momento?", a: "Sim! O cancelamento pode ser feito a qualquer momento e terá efeito ao final do período de cobrança vigente." },
      { q: "Meus dados são mantidos após cancelamento?", a: "Seus dados ficam disponíveis por 90 dias após o cancelamento. Depois disso, são excluídos permanentemente." },
    ],
  },
];

const CentralAjuda = () => {
  const [search, setSearch] = useState("");
  const [openItems, setOpenItems] = useState<Set<string>>(new Set());

  const toggle = (key: string) => {
    setOpenItems((prev) => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  };

  const filteredCategories = faqCategories.map((cat) => ({
    ...cat,
    faqs: cat.faqs.filter((f) =>
      !search || f.q.toLowerCase().includes(search.toLowerCase()) || f.a.toLowerCase().includes(search.toLowerCase())
    ),
  })).filter((cat) => cat.faqs.length > 0);

  return (
    <div className="min-h-screen bg-background">
      <section className="pt-32 pb-24">
        <div className="container max-w-4xl">
          <Link to="/" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-8">
            <ArrowLeft className="h-4 w-4" /> Voltar ao início
          </Link>

          <motion.div className="text-center mb-12" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <h1 className="text-4xl md:text-5xl font-bold mb-4">Central de Ajuda</h1>
            <p className="text-lg text-muted-foreground mb-8">Encontre respostas para as dúvidas mais frequentes.</p>
            <div className="relative max-w-md mx-auto">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
              <Input
                placeholder="Buscar dúvida..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10 h-12 bg-muted border-border"
              />
            </div>
          </motion.div>

          <div className="space-y-10">
            {filteredCategories.map((cat) => (
              <motion.div key={cat.title} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
                <div className="flex items-center gap-3 mb-4">
                  <cat.icon className="h-5 w-5 text-primary" />
                  <h2 className="text-xl font-bold">{cat.title}</h2>
                </div>
                <div className="space-y-2">
                  {cat.faqs.map((faq) => {
                    const key = `${cat.title}-${faq.q}`;
                    const isOpen = openItems.has(key);
                    return (
                      <div key={key} className="rounded-xl border border-border bg-gradient-card overflow-hidden">
                        <button onClick={() => toggle(key)} className="w-full flex items-center justify-between p-4 text-left">
                          <span className="font-medium text-sm">{faq.q}</span>
                          {isOpen ? <ChevronUp className="h-4 w-4 text-muted-foreground shrink-0" /> : <ChevronDown className="h-4 w-4 text-muted-foreground shrink-0" />}
                        </button>
                        {isOpen && (
                          <div className="px-4 pb-4">
                            <p className="text-sm text-muted-foreground leading-relaxed">{faq.a}</p>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </motion.div>
            ))}
          </div>

          <div className="mt-16 grid md:grid-cols-2 gap-6">
            <Link to="/tutoriais" className="p-8 rounded-2xl bg-gradient-card border border-border hover:border-primary/40 hover:shadow-glow transition-all group text-center">
              <BookOpen className="h-8 w-8 text-primary mx-auto mb-3" />
              <h3 className="text-xl font-bold mb-2">Tutoriais Passo a Passo</h3>
              <p className="text-muted-foreground mb-4 text-sm">Guias completos com prints de tela para todas as funcionalidades.</p>
              <span className="text-primary font-medium group-hover:underline">Ver Tutoriais →</span>
            </Link>
            <div className="p-8 rounded-2xl bg-gradient-card border border-border text-center">
              <h3 className="text-xl font-bold mb-2">Não encontrou o que procurava?</h3>
              <p className="text-muted-foreground mb-4 text-sm">Nossa equipe está pronta para ajudar.</p>
              <Link to="/contato" className="inline-flex items-center gap-2 text-primary font-medium hover:underline">
                Fale Conosco →
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CentralAjuda;
